import axios from "axios";
import {
  assigneesLabel,
  contactLabel,
  formatDate,
  getExpandedAssignees,
  getRelationIds,
} from "./taskHelpers";
import {
  getTaskArchivedEmail,
  getTaskAssignedEmail,
  getTaskCommentEmail,
  getTaskCreatedEmail,
  getTaskReopenedEmail,
  getTaskStatusChangedEmail,
} from "../templates/taskEmails";

const EMAIL_QUEUE_URL = "https://alarmtracking.a-traq.com/api/settings/email-queue/public/create";

export const getTaskLink = (taskId) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/tasks?task=${taskId}`;
};

export const queueTaskEmail = async ({ email, subject, htmlMessage, status = "" }) => {
  if (!email?.trim()) return false;

  try {
    await axios.post(EMAIL_QUEUE_URL, {
      Email: email.trim(),
      Subject: subject,
      Message: htmlMessage,
      Status: status || "",
      File: null,
      Site: "",
      Signaldatetime: new Date().toISOString(),
      SendTime: null,
    });
    return true;
  } catch (error) {
    if (error.response) {
      console.error("Error queueing task email:", error.response.status, error.response.data);
    } else {
      console.error("Error queueing task email:", error.message);
    }
    return false;
  }
};

const resolveContact = (contactId, contacts = [], expandedList = []) => {
  if (!contactId) return null;
  const fromExpand = Array.isArray(expandedList)
    ? expandedList.find((c) => c?.id === contactId)
    : expandedList?.id === contactId
      ? expandedList
      : null;
  if (fromExpand?.email) return fromExpand;
  return contacts.find((c) => c.id === contactId) || null;
};

const resolveAssignees = (task, contacts = []) => {
  const ids = getRelationIds(task.AssignedTo, task.expand?.AssignedTo);
  const expanded = getExpandedAssignees(task);
  return ids
    .map((id) => resolveContact(id, contacts, expanded))
    .filter((c) => c?.email);
};

const uniqueRecipients = (contacts, excludeId) => {
  const seen = new Set();
  return contacts.filter((contact) => {
    if (!contact?.email || !contact?.id) return false;
    if (excludeId && contact.id === excludeId) return false;
    if (seen.has(contact.email)) return false;
    seen.add(contact.email);
    return true;
  });
};

const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 280);

const statusName = (statuses, statusId) =>
  statuses.find((s) => s.id === statusId)?.Name || "";

const taskEmailMeta = (task, statuses) => ({
  priority: task.Priority,
  status: statusName(statuses, task.Status) || task.expand?.Status?.Name || "",
  dueDate: task.DueDate ? formatDate(task.DueDate) : "",
  category: task.Category || "",
  assigneeName: assigneesLabel(task),
});

const sendToMany = async (recipients, buildPayload) => {
  await Promise.all(
    recipients.map(async (recipient) => {
      const { subject, html, status } = buildPayload(recipient);
      await queueTaskEmail({ email: recipient.email, subject, htmlMessage: html, status });
    })
  );
};

/** New task → notify all assignees */
export const notifyTaskCreated = async ({
  task,
  contacts = [],
  statuses = [],
  actorId,
}) => {
  const assignees = resolveAssignees(task, contacts);
  if (assignees.length === 0) return;

  const recipients = uniqueRecipients(assignees, actorId);
  const creatorName = contactLabel(
    resolveContact(task.CreatedBy, contacts, task.expand?.CreatedBy) || {}
  );
  const meta = taskEmailMeta(task, statuses);

  await sendToMany(recipients, (recipient) => ({
    subject: `New task assigned: ${task.Title}`,
    status: meta.status,
    html: getTaskCreatedEmail({
      recipientName: contactLabel(recipient),
      taskTitle: task.Title,
      ...meta,
      creatorName,
      taskLink: getTaskLink(task.id),
    }),
  }));
};

/** New assignee(s) added → notify each new assignee */
export const notifyTaskAssigned = async ({
  task,
  contacts = [],
  statuses = [],
  actorId,
  assigneeIds = [],
}) => {
  const ids = Array.isArray(assigneeIds) ? assigneeIds : [assigneeIds].filter(Boolean);
  if (ids.length === 0) return;

  const assignees = ids
    .map((id) => resolveContact(id, contacts, task.expand?.AssignedTo))
    .filter((c) => c?.email);

  const recipients = uniqueRecipients(assignees, actorId);
  const actorName = contactLabel(contacts.find((c) => c.id === actorId) || {});
  const meta = taskEmailMeta(task, statuses);

  await sendToMany(recipients, (recipient) => ({
    subject: `Task assigned to you: ${task.Title}`,
    status: meta.status,
    html: getTaskAssignedEmail({
      recipientName: contactLabel(recipient),
      taskTitle: task.Title,
      ...meta,
      actorName,
      taskLink: getTaskLink(task.id),
    }),
  }));
};

/** Status changed → notify task creator */
export const notifyTaskStatusChanged = async ({
  task,
  contacts = [],
  statuses = [],
  actorId,
  oldStatusName,
  newStatusName,
}) => {
  const creator = resolveContact(task.CreatedBy, contacts, task.expand?.CreatedBy);
  if (!creator?.email) return;

  const recipients = uniqueRecipients([creator], actorId);
  const actorName = contactLabel(contacts.find((c) => c.id === actorId) || {});
  const meta = taskEmailMeta(task, statuses);

  await sendToMany(recipients, (recipient) => ({
    subject: `Task status updated: ${task.Title}`,
    status: newStatusName,
    html: getTaskStatusChangedEmail({
      recipientName: contactLabel(recipient),
      taskTitle: task.Title,
      oldStatus: oldStatusName,
      newStatus: newStatusName,
      priority: meta.priority,
      category: meta.category,
      actorName,
      taskLink: getTaskLink(task.id),
    }),
  }));
};

/** Comment posted → notify assignees + creator (except poster) */
export const notifyTaskComment = async ({
  task,
  contacts = [],
  actorId,
  commentBody,
}) => {
  const assignees = resolveAssignees(task, contacts);
  const creator = resolveContact(task.CreatedBy, contacts, task.expand?.CreatedBy);

  const recipients = uniqueRecipients(
    [...assignees, creator].filter(Boolean),
    actorId
  );

  const commentAuthor = contactLabel(contacts.find((c) => c.id === actorId) || {});
  const commentPreview = stripHtml(commentBody) || "(See task for details)";

  await sendToMany(recipients, (recipient) => ({
    subject: `New comment on task: ${task.Title}`,
    status: task.expand?.Status?.Name || "",
    html: getTaskCommentEmail({
      recipientName: contactLabel(recipient),
      taskTitle: task.Title,
      category: task.Category || "",
      commentAuthor,
      commentPreview,
      taskLink: getTaskLink(task.id),
    }),
  }));
};

/** Task reopened → notify all assignees */
export const notifyTaskReopened = async ({
  task,
  contacts = [],
  statuses = [],
  actorId,
  newStatusName,
}) => {
  const assignees = resolveAssignees(task, contacts);
  if (assignees.length === 0) return;

  const recipients = uniqueRecipients(assignees, actorId);
  const actorName = contactLabel(contacts.find((c) => c.id === actorId) || {});

  await sendToMany(recipients, (recipient) => ({
    subject: `Task reopened: ${task.Title}`,
    status: newStatusName,
    html: getTaskReopenedEmail({
      recipientName: contactLabel(recipient),
      taskTitle: task.Title,
      category: task.Category || "",
      status: newStatusName,
      actorName,
      taskLink: getTaskLink(task.id),
    }),
  }));
};

/** Task archived → notify assignees + creator */
export const notifyTaskArchived = async ({ task, contacts = [], actorId }) => {
  const assignees = resolveAssignees(task, contacts);
  const creator = resolveContact(task.CreatedBy, contacts, task.expand?.CreatedBy);

  const recipients = uniqueRecipients(
    [...assignees, creator].filter(Boolean),
    actorId
  );

  const actorName = contactLabel(contacts.find((c) => c.id === actorId) || {});

  await sendToMany(recipients, (recipient) => ({
    subject: `Task archived: ${task.Title}`,
    status: "Archived",
    html: getTaskArchivedEmail({
      recipientName: contactLabel(recipient),
      taskTitle: task.Title,
      category: task.Category || "",
      actorName,
      taskLink: getTaskLink(task.id),
    }),
  }));
};

export const fireTaskNotification = (fn) => {
  fn().catch((error) => console.error("Task notification failed:", error));
};
